import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import api from '../../api';

const CreateCampaignModal = ({ isOpen, onClose, onCreated, userId }) => {
  const [title, setTitle] = useState('');
  const [budget, setBudget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [logo, setLogo] = useState(null);
  const [targetState, setTargetState] = useState('');
  const [targetCity, setTargetCity] = useState('');
  const [targetInterests, setTargetInterests] = useState([]);
  const [requiredPlatforms, setRequiredPlatforms] = useState([]);
  const [states, setStates] = useState([]);
  const [interests, setInterests] = useState([]);
  const [interestSearch, setInterestSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch states and interests on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await api.get('/states');
        if (response.data && Array.isArray(response.data)) {
          setStates(response.data);
        }
      } catch (err) {
        setStates([]);
      }
    };

    const fetchInterests = async () => {
      try {
        const response = await api.get('/interests/grouped');

        // Convert grouped interests to flat array
        const allInterests = [];
        Object.entries(response.data).forEach(([category, items]) => {
          items.forEach(item => {
            allInterests.push({ category, name: item, id: `${category}-${item}` });
          });
        });

        setInterests(allInterests);
      } catch (err) {
        setInterests([]);
      }
    };

    if (isOpen) {
      fetchStates();
      fetchInterests();
    }
  }, [isOpen]);

  // Add interest to selected list
  const addInterest = (interest) => {
    if (!targetInterests.includes(interest.name)) {
      setTargetInterests(prev => [...prev, interest.name]);
    }
    setInterestSearch('');
  };

  // Remove interest from selected list
  const removeInterest = (interestToRemove) => {
    setTargetInterests(prev => prev.filter(interest => interest !== interestToRemove));
  };

  // Toggle platform selection
  const togglePlatform = (platform) => {
    if (requiredPlatforms.includes(platform)) {
      setRequiredPlatforms(prev => prev.filter(p => p !== platform));
    } else {
      setRequiredPlatforms(prev => [...prev, platform]);
    }
  };

  // Filter interests based on search and category
  const filteredInterests = interests.filter(interest => {
    const matchesSearch = interest.name.toLowerCase().includes(interestSearch.toLowerCase());
    const matchesCategory = !selectedCategory || interest.category === selectedCategory;
    const notSelected = !targetInterests.includes(interest.name);
    return matchesSearch && matchesCategory && notSelected;
  });

  // Get unique categories from interests
  const categories = [...new Set(interests.map(i => i.category))].sort();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate userId is present
    if (!userId) {
      setError('Session expired. Please refresh the page and login again.');
      return;
    }


    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('budget', budget);
      formData.append('deadline', deadline);

      // Always append targeting fields (backend handles null/empty)
      if (targetState && targetState.trim() !== '') {
        formData.append('targetState', targetState);
      }
      if (targetCity && targetCity.trim() !== '') {
        formData.append('targetCity', targetCity);
      }
      if (targetInterests && targetInterests.length > 0) {
        formData.append('targetInterests', targetInterests.join(','));
      }
      if (requiredPlatforms && requiredPlatforms.length > 0) {
        formData.append('requiredPlatforms', requiredPlatforms.join(','));
      }

      if (logo) formData.append('logo', logo);

      // POST to your backend endpoint
      const response = await api.post(`/brands/${userId}/campaigns`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });


      // Reset form
      setTitle('');
      setBudget('');
      setDeadline('');
      setTargetState('');
      setTargetCity('');
      setTargetInterests([]);
      setRequiredPlatforms([]);
      setLogo(null);

      onCreated(); // Refresh campaigns in parent
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data || 'Failed to create campaign. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Create New Campaign</h2>

        {/* Warning if userId is missing */}
        {!userId && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Session Error</p>
            <p className="text-sm">User session not found. Please refresh the page and login again.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Title</label>
            <input
              type="text"
              placeholder="Campaign Title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              className="w-full border rounded p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget (₹)</label>
            <input
              type="number"
              placeholder="Budget in rupees"
              value={budget}
              onChange={e => setBudget(e.target.value)}
              required
              className="w-full border rounded p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bidding End Date</label>
            <input
              type="date"
              placeholder="Bidding End Date"
              value={deadline}
              onChange={e => setDeadline(e.target.value)}
              required
              className="w-full border rounded p-2"
              title="Last date for Creators to submit bids"
            />
          </div>

          {/* Targeting Criteria Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-md font-semibold text-gray-800 mb-3">Targeting Criteria (Optional)</h3>
            <p className="text-xs text-gray-500 mb-3">
              Filter which Creators can see and bid on this campaign based on their location and interests
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target State
                </label>
                <select
                  value={targetState}
                  onChange={e => {
                    setTargetState(e.target.value);
                    setTargetCity(''); // Reset city when state changes
                  }}
                  className="w-full border rounded p-2 bg-white"
                >
                  <option value="">All States (No Filter)</option>
                  {states.length === 0 && (
                    <option value="" disabled>Loading states...</option>
                  )}
                  {states.map((state) => (
                    <option key={state.id} value={state.stateName}>
                      {state.stateName}
                    </option>
                  ))}
                </select>
                {states.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Unable to load states. Please refresh the page.
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Select a state to show this campaign only to creators from that state
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target City
                </label>
                <input
                  type="text"
                  placeholder="Enter city name (optional)"
                  value={targetCity}
                  onChange={e => setTargetCity(e.target.value)}
                  className="w-full border rounded p-2"
                  disabled={!targetState}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {targetState
                    ? "Further filter by specific city within the selected state"
                    : "Select a state first to enable city filtering"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Interests
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select interests to target creators. Campaign will be shown to creators with ANY of the selected interests.
                </p>

                {/* Category Filters */}
                {interests.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('')}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        selectedCategory === '' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All Categories
                    </button>
                    {categories.map(category => (
                      <button
                        type="button"
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          selectedCategory === category 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}

                {/* Search Input */}
                {interests.length > 0 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={interestSearch}
                      onChange={(e) => setInterestSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Search interests..."
                    />
                  </div>
                )}

                {/* Available Interests to Add */}
                {(interestSearch || selectedCategory) && filteredInterests.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto mb-3">
                    {Object.entries(
                      filteredInterests.reduce((acc, interest) => {
                        if (!acc[interest.category]) acc[interest.category] = [];
                        acc[interest.category].push(interest);
                        return acc;
                      }, {})
                    ).map(([category, categoryInterests]) => (
                      <div key={category} className="mb-3 last:mb-0">
                        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs mr-2">{category}</span>
                          <span className="text-gray-500 text-xs">({categoryInterests.length})</span>
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categoryInterests.map(interest => (
                            <button
                              type="button"
                              key={interest.id}
                              onClick={() => addInterest(interest)}
                              className="px-2.5 py-1 text-xs bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-700 rounded-lg transition-colors"
                            >
                              {interest.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No Results Message */}
                {(interestSearch || selectedCategory) && filteredInterests.length === 0 && interests.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3 text-center text-gray-500 text-xs mb-3">
                    No interests found. Try a different search or category.
                  </div>
                )}

                {/* Loading State */}
                {interests.length === 0 && (
                  <div className="text-center py-3 text-gray-500 text-xs">
                    Loading interests...
                  </div>
                )}

                {/* Selected Interests */}
                {targetInterests.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Selected Interests ({targetInterests.length}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {targetInterests.map((interest) => {
                        const interestObj = interests.find(i => i.name === interest);
                        const categoryTag = interestObj ? interestObj.category : '';

                        return (
                          <span
                            key={interest}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                          >
                            {categoryTag && (
                              <span className="text-xs font-semibold mr-1 px-1.5 py-0.5 bg-blue-200 rounded">
                                {categoryTag}
                              </span>
                            )}
                            {interest}
                            <button
                              type="button"
                              onClick={() => removeInterest(interest)}
                              className="ml-1.5 hover:text-blue-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {targetInterests.length === 0 && (
                  <p className="text-xs text-gray-500 italic">
                    No interests selected. Campaign will be visible to all creators (subject to location filters).
                  </p>
                )}
              </div>

              {/* Social Media Platform Requirements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Social Media Platforms
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select platforms that Creators must have connected. Only Creators with at least one of these platforms will be able to bid.
                </p>

                <div className="flex flex-wrap gap-4">
                  {/* YouTube */}
                  <button
                    type="button"
                    onClick={() => togglePlatform('youtube')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      requiredPlatforms.includes('youtube')
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="YouTube"
                  >
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('youtube') ? '#FF0000' : '#9CA3AF'}>
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    <span className={`text-xs mt-1 ${requiredPlatforms.includes('youtube') ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                      YouTube
                    </span>
                  </button>

                  {/* Instagram */}
                  <button
                    type="button"
                    onClick={() => togglePlatform('instagram')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      requiredPlatforms.includes('instagram')
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="Instagram"
                  >
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('instagram') ? '#E4405F' : '#9CA3AF'}>
                      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                    </svg>
                    <span className={`text-xs mt-1 ${requiredPlatforms.includes('instagram') ? 'text-pink-600 font-semibold' : 'text-gray-600'}`}>
                      Instagram
                    </span>
                  </button>

                  {/* Facebook */}
                  <button
                    type="button"
                    onClick={() => togglePlatform('facebook')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      requiredPlatforms.includes('facebook')
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="Facebook"
                  >
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('facebook') ? '#1877F2' : '#9CA3AF'}>
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span className={`text-xs mt-1 ${requiredPlatforms.includes('facebook') ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}>
                      Facebook
                    </span>
                  </button>

                  {/* Twitter/X */}
                  <button
                    type="button"
                    onClick={() => togglePlatform('twitter')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      requiredPlatforms.includes('twitter')
                        ? 'border-black bg-gray-100'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="Twitter/X"
                  >
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('twitter') ? '#000000' : '#9CA3AF'}>
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span className={`text-xs mt-1 ${requiredPlatforms.includes('twitter') ? 'text-black font-semibold' : 'text-gray-600'}`}>
                      Twitter/X
                    </span>
                  </button>

                  {/* LinkedIn */}
                  <button
                    type="button"
                    onClick={() => togglePlatform('linkedin')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      requiredPlatforms.includes('linkedin')
                        ? 'border-blue-700 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="LinkedIn"
                  >
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('linkedin') ? '#0A66C2' : '#9CA3AF'}>
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span className={`text-xs mt-1 ${requiredPlatforms.includes('linkedin') ? 'text-blue-700 font-semibold' : 'text-gray-600'}`}>
                      LinkedIn
                    </span>
                  </button>

                  {/* TikTok */}
                  <button
                    type="button"
                    onClick={() => togglePlatform('tiktok')}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      requiredPlatforms.includes('tiktok')
                        ? 'border-black bg-gray-100'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="TikTok"
                  >
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('tiktok') ? '#000000' : '#9CA3AF'}>
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                    <span className={`text-xs mt-1 ${requiredPlatforms.includes('tiktok') ? 'text-black font-semibold' : 'text-gray-600'}`}>
                      TikTok
                    </span>
                  </button>
                </div>

                {requiredPlatforms.length === 0 && (
                  <p className="text-xs text-gray-500 italic mt-3">
                    No platform selected. All Creators can bid regardless of connected platforms.
                  </p>
                )}

                {requiredPlatforms.length > 0 && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-800">
                      <strong>Selected:</strong> Only Creators with {requiredPlatforms.join(', ')} will be able to bid on this campaign.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Logo (Optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setLogo(e.target.files[0])}
              className="w-full"
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-gray-600 hover:underline px-3 py-2">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;
